import React, { useState, useEffect, useRef } from 'react'
import { State } from '../../../types/state'
import { useSelector, useDispatch } from 'react-redux'
import { Package } from '../../../enums/package.enum'
import { useRequests } from '../../../requests/useRequests'
import { Project } from '../../../types/project'
import ItemCommentsModal from './components/ItemCommentsModal'
import ItemActions from './components/ItemActions'
import { PROJECT_CATEGORIES } from './components/ItemModal'
import ItemImageModal from './components/ItemImageModal'

interface ItemsListProps {
  project: Project
  openEditModal: (itemId: string) => void
  handleDeleteItem: (itemId: string) => Promise<void>
  handleApproveItem: (itemId: string) => Promise<void>
  handleRejectItem: (itemId: string) => Promise<void>
  handleCopyItem: (itemId: string) => Promise<void>
  handleMoveItemUp: (itemId: string, stageId: string) => Promise<void>
  handleMoveItemDown: (itemId: string, stageId: string) => Promise<void>
  readOnly?: boolean
  selectedColumns?: { [key: string]: boolean }
  showAllColumns: boolean
  globalCategoryFilter?: string
  globalStatusFilter?: string[]
  globalStageFilter?: string
  onProjectUpdate?: (project: Project) => void
  stageAttachments?: { [stageId: string]: any | null }
}

interface ItemComment {
  id: string
  content: string
  createdAt: string
  updatedAt: string
  authorId: string
  authorName: string
}

const ItemsList: React.FC<ItemsListProps> = ({
  project,
  openEditModal,
  handleDeleteItem,
  handleApproveItem,
  handleRejectItem,
  handleCopyItem,
  handleMoveItemUp,
  handleMoveItemDown,
  readOnly = false,
  selectedColumns = {},
  showAllColumns = false,
  globalCategoryFilter,
  globalStatusFilter,
  globalStageFilter,
  onProjectUpdate,
  stageAttachments
}) => {
  const { package: userPackage, packageExpiredAt } = useSelector((state: State) => state.user)
  const requestMaker = useRequests()

  const [commentsCount, setCommentsCount] = useState<{ [key: string]: number }>({})
  const [showCommentsModal, setShowCommentsModal] = useState(false)
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [selectedItemName, setSelectedItemName] = useState<string>('')
  const [showImageModal, setShowImageModal] = useState(false)
  const [selectedItemForImage, setSelectedItemForImage] = useState<{ id: string, imageUrl?: string } | null>(null)
  const [hoveredImageId, setHoveredImageId] = useState<string | null>(null)

  const fetchAllCommentsCount = async () => {
    try {
      const counts: { [key: string]: number } = {}

      for (const item of project.items) {
        try {
          const response = await requestMaker.getProjectItemComments(project.id, item.id)
          counts[item.id] = response.data.length
        } catch (error) {
          console.error(`Błąd pobierania komentarzy dla elementu ${item.id}`, error)
          counts[item.id] = 0
        }
      }

      setCommentsCount(counts)
    } catch (error) {
      console.error('Błąd podczas pobierania liczby komentarzy', error)
    }
  }

  const handleOpenComments = (itemId: string, itemName?: string) => {
    setSelectedItemId(itemId)
    setSelectedItemName(itemName || '')
    setShowCommentsModal(true)
  }

  const handleCloseComments = () => {
    setShowCommentsModal(false)
    setSelectedItemId(null)
    setSelectedItemName('')
  }

  const handleOpenImageModal = (itemId: string, imageUrl?: string) => {
    setSelectedItemForImage({ id: itemId, imageUrl })
    setShowImageModal(true)
  }

  const handleCloseImageModal = () => {
    setShowImageModal(false)
    setSelectedItemForImage(null)
  }

  const handleImageSave = () => {
    handleCloseImageModal()
    // Odśwież listę elementów
    if (project) {
      fetchAllCommentsCount()
    }
  }

  const calculateTotalPrice = () => {
    if (project.items.length === 0) return 0
    return project.items.reduce((total, item) => {
      const basePrice = item.price * item.quantity
      const withDelivery = basePrice + (item.deliveryCost || 0)
      const withMargin = withDelivery * (1 + ((item.marginPercentage || 0) / 100))
      const withVat = withMargin * (1 + ((item.vatPercentage || 0) / 100))
      const withDiscount = withVat * (1 - ((item.discountPercentage || 0) / 100))
      return total + withDiscount
    }, 0)
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <span className='badge badge-success'>Zaakceptowano</span>
      case 'rejected':
        return <span className='badge badge-danger'>Odrzucono</span>
      default:
        return <span className='badge badge-warning'>Oczekujący</span>
    }
  }

  const handleToggleVisibility = async (itemId: string, isHidden: boolean) => {
    try {
      await requestMaker.updateProjectItemVisibility(project.id, itemId, isHidden)
      // Odśwież listę elementów
      const response = await requestMaker.getProject(project.id)
      if (response.data) {
        // Aktualizuj stan projektu
        project.items = response.data.items
        // Wywołaj callback do odświeżenia stanu w komponencie nadrzędnym
        if (onProjectUpdate != null) {
          onProjectUpdate(response.data)
        }
      }
    } catch (error) {
      console.error('Błąd podczas aktualizacji widoczności elementu', error)
    }
  }

  return (
    <div className='items-list'>
      {(globalStageFilter
        ? project.stages.filter(stage => stage.name === globalStageFilter)
        : project.stages
      ).map((stage) => (
        <div key={stage.id} className='stage-items mb-30'>
          <h5 className='mb-3'>
            {stage.name}
            {stageAttachments?.[stage.id] && (
              <a
                href={stageAttachments[stage.id].presignedUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='ml-2'
                title={`Pobierz załącznik: ${stageAttachments[stage.id].name}`}
              >
                {stageAttachments[stage.id].type.startsWith('image/')
                  ? (
                    <img
                      src={stageAttachments[stage.id].presignedUrl}
                      alt={stageAttachments[stage.id].name}
                      style={{
                        maxHeight: '100px',
                        maxWidth: '150px',
                        objectFit: 'contain',
                        marginLeft: 8,
                        verticalAlign: 'middle',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    />
                    )
                  : (
                    <i className='fa fa-paperclip' style={{ fontSize: 18, marginLeft: 8 }} />
                    )}
              </a>
            )}
          </h5>
          <div
            className='items-container'
            style={{
              overflowX: 'auto',
              width: '100%'
            }}
          >
            <table className='table table-hover'>
              <thead>
                <tr>
                  {(showAllColumns || selectedColumns.number) && <th style={{ width: '40px' }}>Lp.</th>}
                  {(showAllColumns || selectedColumns.stage) && <th>Etap</th>}
                  {(showAllColumns || selectedColumns.image) && <th style={{ width: '100px' }}>Zdjęcie</th>}
                  {(showAllColumns || selectedColumns.name) && <th>Nazwa</th>}
                  {(showAllColumns || selectedColumns.type) && <th>Typ</th>}
                  {(showAllColumns || selectedColumns.description) && <th>Opis</th>}
                  {(showAllColumns || selectedColumns.url) && <th>URL</th>}
                  {(showAllColumns || selectedColumns.dimensions) && <th>Wymiary</th>}
                  {(showAllColumns || selectedColumns.price) && <th style={{ width: '120px' }}>Cena (PLN)</th>}
                  {(showAllColumns || selectedColumns.quantity) && <th style={{ width: '80px' }}>Ilość</th>}
                  {(showAllColumns || selectedColumns.unit) && <th style={{ width: '90px' }}>Jednostka</th>}
                  {(showAllColumns || selectedColumns.deliveryCost) && <th style={{ width: '120px' }}>Dostawa (PLN)</th>}
                  {(showAllColumns || selectedColumns.marginPercentage) && <th style={{ width: '90px' }}>Marża (%)</th>}
                  {(showAllColumns || selectedColumns.vatPercentage) && <th style={{ width: '90px' }}>VAT (%)</th>}
                  {(showAllColumns || selectedColumns.discountPercentage) && <th style={{ width: '90px' }}>Rabat (%)</th>}
                  {(showAllColumns || selectedColumns.value) && <th style={{ width: '120px' }}>Wartość (PLN)</th>}
                  {(showAllColumns || selectedColumns.status) && <th>Status</th>}
                  {(showAllColumns || selectedColumns.category) && <th>Kategoria</th>}
                  {(showAllColumns || selectedColumns.catalogNumber) && <th>Nr katalogowy</th>}
                  {(showAllColumns || selectedColumns.supplier) && <th>Dostawca</th>}
                  <th>Akcje</th>
                  <th>Komentarze</th>
                </tr>
              </thead>
              <tbody>
                {project.items
                  .filter((item) => {
                    if (!globalStageFilter) return true
                    const stage = project.stages.find(s => s.id === item.stageId)
                    return (stage != null) && stage.name === globalStageFilter
                  })
                  .filter((item) => item.stageId === stage.id)
                  .map((item, index) => (
                    <tr
                      key={item.id}
                      className={`item-row ${
                        item.status === 'approved'
                          ? 'table-success'
                          : item.status === 'rejected'
                            ? 'table-danger'
                            : ''
                      } ${item.isHidden ? 'opacity-50' : ''}`}
                    >
                      {(showAllColumns || selectedColumns.number) && <td>{index + 1}</td>}
                      {(showAllColumns || selectedColumns.stage) && <td>{stage.name}</td>}
                      {(showAllColumns || selectedColumns.image) && (
                        <td className='text-center'>
                          <div
                            className='position-relative'
                            style={{ display: 'inline-block' }}
                            onMouseEnter={() => setHoveredImageId(item.id)}
                            onMouseLeave={() => setHoveredImageId(null)}
                          >
                            {item.imageUrl
                              ? (
                                <div className='image-container' style={{ position: 'relative' }}>
                                  <img
                                    src={item.imageUrl}
                                    alt={item.name}
                                    style={{
                                      maxHeight: '70px',
                                      maxWidth: '80px',
                                      objectFit: 'contain'
                                    }}
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none'
                                      e.currentTarget.parentElement!.innerHTML = '<i class="fa fa-image fa-2x text-muted"></i>'
                                    }}
                                  />
                                  {!readOnly && (
                                    <div
                                      className='image-overlay'
                                      style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        opacity: hoveredImageId === item.id ? 1 : 0,
                                        transition: 'opacity 0.2s',
                                        cursor: 'pointer',
                                        zIndex: 2
                                      }}
                                      onClick={() => handleOpenImageModal(item.id, item.imageUrl)}
                                    >
                                      <i className='fa fa-plus' style={{ color: 'white', fontSize: '20px' }} />
                                    </div>
                                  )}
                                </div>
                                )
                              : (
                                <div
                                  className='no-image-container'
                                  style={{
                                    width: '80px',
                                    height: '70px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '4px',
                                    position: 'relative'
                                  }}
                                >
                                  <i className='fa fa-image fa-2x text-muted' />
                                  {!readOnly && (
                                    <div
                                      className='image-overlay'
                                      style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        opacity: hoveredImageId === item.id ? 1 : 0,
                                        transition: 'opacity 0.2s',
                                        cursor: 'pointer',
                                        borderRadius: '4px',
                                        zIndex: 2
                                      }}
                                      onClick={() => handleOpenImageModal(item.id)}
                                    >
                                      <i className='fa fa-plus' style={{ color: 'white', fontSize: '20px' }} />
                                    </div>
                                  )}
                                </div>
                                )}
                          </div>
                        </td>
                      )}
                      {(showAllColumns || selectedColumns.name) && <td>{item.name}</td>}
                      {(showAllColumns || selectedColumns.type) && <td>{
                        item.type === 'product'
                          ? 'Produkt'
                          : item.type === 'service'
                            ? 'Usługa'
                            : item.type === 'note'
                              ? 'Notatka'
                              : item.type === 'visualization'
                                ? 'Wizualizacja'
                                : item.type === 'attachment' ? 'Załącznik' : '-'
                      }
                      </td>}
                      {(showAllColumns || selectedColumns.description) && <td>{item.description.length > 100 ? item.description.substring(0, 100) + '...' : item.description || '-'}</td>}
                      {(showAllColumns || selectedColumns.url) && (
                        <td>
                          {item.url
                            ? (
                              <a href={item.url} target='_blank' rel='noopener noreferrer' className='text-primary'>
                                <i className='fas fa-external-link-alt mr-1' />Link
                              </a>
                              )
                            : '-'}
                        </td>
                      )}
                      {(showAllColumns || selectedColumns.dimensions) && <td>{item.dimensions || '-'}</td>}
                      {(showAllColumns || selectedColumns.price) && <td className='text-right'>{item.price.toFixed(2)}</td>}
                      {(showAllColumns || selectedColumns.quantity) && <td>{item.quantity}</td>}
                      {(showAllColumns || selectedColumns.unit) && <td>{item.unit}</td>}
                      {(showAllColumns || selectedColumns.deliveryCost) && <td className='text-right'>{(item.deliveryCost || 0).toFixed(2)}</td>}
                      {(showAllColumns || selectedColumns.marginPercentage) && <td className='text-right'>{(item.marginPercentage || 0).toFixed(1)}</td>}
                      {(showAllColumns || selectedColumns.vatPercentage) && <td className='text-right'>{(item.vatPercentage || 0).toFixed(1)}</td>}
                      {(showAllColumns || selectedColumns.discountPercentage) && <td className='text-right'>{(item.discountPercentage || 0).toFixed(1)}</td>}
                      {(showAllColumns || selectedColumns.value) && (
                        <td className='text-right'>
                          {(() => {
                            const basePrice = item.price * item.quantity
                            const withDelivery = basePrice + (item.deliveryCost || 0)
                            const withMargin = withDelivery * (1 + ((item.marginPercentage || 0) / 100))
                            const withVat = withMargin * (1 + ((item.vatPercentage || 0) / 100))
                            const withDiscount = withVat * (1 - ((item.discountPercentage || 0) / 100))
                            return withDiscount.toFixed(2)
                          })()}
                        </td>
                      )}
                      {(showAllColumns || selectedColumns.status) && <td>{getStatusBadge(item.status || 'pending')}</td>}
                      {(showAllColumns || selectedColumns.category) && <td>{PROJECT_CATEGORIES.find((category: { id: string, label: string }) => category.id === item.category)?.label || '-'}</td>}
                      {(showAllColumns || selectedColumns.catalogNumber) && <td>{item.catalogNumber}</td>}
                      {(showAllColumns || selectedColumns.supplier) && <td>{item.supplier}</td>}
                      <td>
                        <ItemActions
                          itemId={item.id}
                          stageId={stage.id}
                          status={item.status}
                          index={index}
                          totalItems={project.items.filter(i => i.stageId === stage.id).length}
                          userPackage={userPackage as Package}
                          packageExpiredAt={(packageExpiredAt != null) ? new Date(packageExpiredAt) : null}
                          readOnly={readOnly}
                          onEdit={openEditModal}
                          onDelete={handleDeleteItem}
                          onApprove={handleApproveItem}
                          onReject={handleRejectItem}
                          onCopy={handleCopyItem}
                          onMoveUp={handleMoveItemUp}
                          onMoveDown={handleMoveItemDown}
                          onToggleVisibility={async () => await handleToggleVisibility(item.id, !item.isHidden)}
                          isHidden={item.isHidden}
                        />
                      </td>
                      <td>
                        <button
                          className='tw-text-blue-500 focus:tw-text-blue-500 tw-px-2 tw-py-1 hover:tw-bg-blue-500/30 hover:tw-rounded-md'
                          onClick={() => handleOpenComments(item.id, item.name)}
                          title='Pokaż komentarze'
                        >
                          <i className='fa fa-comment' />
                          {commentsCount[item.id] > 0 && (
                            <span className='badge badge-light ml-1'>{commentsCount[item.id]}</span>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div className='card-body'>
        <div className='row'>
          <div className='col-md-10 text-right'>
            <h5>Razem:</h5>
          </div>
          <div className='col-md-2'>
            <h5>{calculateTotalPrice().toFixed(2)} PLN</h5>
          </div>
        </div>
      </div>

      {showCommentsModal && selectedItemId && (
        <ItemCommentsModal
          show={showCommentsModal}
          onClose={handleCloseComments}
          projectId={project.id}
          itemId={selectedItemId}
          itemName={selectedItemName}
        />
      )}

      {(selectedItemForImage != null) && (
        <ItemImageModal
          show={showImageModal}
          onClose={handleCloseImageModal}
          onSave={handleImageSave}
          itemId={selectedItemForImage.id}
          projectId={project.id}
          currentImageUrl={selectedItemForImage.imageUrl}
        />
      )}
    </div>
  )
}

export default ItemsList
